import { cellIndex, occupants, rowsOf } from './board';
import { correctEdges, edgeKey } from './edges';
import { computeGroups, unitOf } from './groups';
import { reveal } from './mods';
import type { Board, Cell, Move, MoveResult, PuzzleEvent, TileMove } from './types';

export function applyMove(board: Board, move: Move): MoveResult {
  const { n } = board;
  const rows = rowsOf(board);
  if (!Number.isInteger(move.anchor) || move.anchor < 0 || move.anchor >= n * rows) {
    throw new Error(`applyMove: invalid anchor ${move.anchor}`);
  }
  if (!Number.isInteger(move.to.row) || !Number.isInteger(move.to.col)) {
    throw new Error(`applyMove: invalid target (${move.to.row}, ${move.to.col})`);
  }
  const unit = unitOf(board, move.anchor);
  const from = board.pos[move.anchor];
  const dr = move.to.row - from.row;
  const dc = move.to.col - from.col;
  const unchanged = (reason: 'out-of-bounds' | 'no-op'): MoveResult => ({
    board,
    groups: computeGroups(board),
    events: [{ type: 'invalid', reason }],
  });
  if (dr === 0 && dc === 0) return unchanged('no-op');

  const dest = unit.map((tile) => ({ tile, to: { row: board.pos[tile].row + dr, col: board.pos[tile].col + dc } }));
  if (dest.some(({ to }) => to.row < 0 || to.col < 0 || to.row >= rows || to.col >= n)) return unchanged('out-of-bounds');

  const occupant = occupants(board);
  const inUnit = new Set(unit);
  const newCells = new Set(dest.map(({ to }) => cellIndex(to, n)));
  const reading = (a: Cell, b: Cell) => cellIndex(a, n) - cellIndex(b, n);
  // 空出的格与被挤开的块都按阅读顺序排序后一一配对；两者数量必然相等
  const vacated = unit.map((t) => board.pos[t]).filter((c) => !newCells.has(cellIndex(c, n))).sort(reading);
  const displaced = [...newCells]
    .map((k) => occupant[k])
    .filter((t) => !inUnit.has(t))
    .sort((a, b) => reading(board.pos[a], board.pos[b]));

  const pos = board.pos.map((c) => ({ ...c }));
  const moves: TileMove[] = [];
  for (const { tile, to } of dest) {
    moves.push({ tile, from: board.pos[tile], to });
    pos[tile] = to;
  }
  displaced.forEach((tile, i) => {
    const to = vacated[i];
    moves.push({ tile, from: board.pos[tile], to });
    pos[tile] = { ...to };
  });
  // 旋转状态跟着块走；背面块按翻开规则在落位后立刻翻开（翻开也可能带来新粘连）
  const moved: Board = { n, pos };
  if (board.rows !== undefined) moved.rows = board.rows;
  if (board.rot) moved.rot = board.rot;
  if (board.down) moved.down = board.down;
  const flipped = reveal(moved);
  const next: Board = flipped.revealed.length ? { ...moved, down: flipped.down } : moved;

  const beforeEdges = correctEdges(board);
  const afterEdges = correctEdges(next);
  const before = new Set(beforeEdges.map(edgeKey));
  const after = new Set(afterEdges.map(edgeKey));
  const newEdges = afterEdges.filter((e) => !before.has(edgeKey(e)));
  const brokenEdges = beforeEdges.filter((e) => !after.has(edgeKey(e)));
  const groups = computeGroups(next);

  const events: PuzzleEvent[] = [{ type: 'moved', moves }];
  if (flipped.revealed.length) events.push({ type: 'revealed', tiles: flipped.revealed });
  if (brokenEdges.length) events.push({ type: 'split', brokenEdges });
  if (newEdges.length) events.push({ type: 'merged', newEdges, groups });
  // spec §5.2：completed ⇔ 最大组 = 块数。"全图一组 ⇔ 完全复原" 由性质测试独立验证
  if (groups.some((g) => g.length === n * rows)) events.push({ type: 'completed' });
  return { board: next, groups, events };
}
